/**
 * @fileoverview PageInfo component for displaying error messages with optional icon
 * @module PageInfo
 * @requires prop-types
 * @requires @smarterservices/smarter-ui
 * @requires ../SinglePane
 */

import PropTypes from 'prop-types';
import { Flex, View, Text } from '@smarterservices/smarter-ui';
import SinglePane from '../SinglePane';
import StepInstructions from '../Common/StepInstructions';

/**
 * A component for displaying error messages with an optional icon
 *
 * @function PageInfo
 * @param {Object} props - The component props
 * @param {string} props.content - The content to display, can be a string or array of objects using BlockKit
 * @param {Object} [props.replacements={}] - Replacements for the content
 * @param {React.ReactNode} [props.icon=null] - Icon to display
 * @param {string} [props.maxWidth='550px'] - Maximum width of the content
 * @returns {React.ReactElement} The rendered component
 */
const PageInfo = ({
  content, 
  replacements = {},
  icon = null,
  maxWidth = '550px'}) => {
  /**
   * Renders the error message with optional icon
   */
  return (
    <SinglePane>
      <Flex as="div" direction="column" alignItems="center" justifyContent="center" height="100%" width="100%">
        <View
          as="div"
          margin="auto"
          textAlign="center"
          maxWidth={maxWidth}
        >
          <View
            as="div"
            className="page-error-content"
            margin="auto"
            textAlign="center"
            borderRadius="large"
            padding="large"
            borderWidth="small"
            borderColor="primary"
            background="primary"
            maxWidth={maxWidth}
          >
            {icon && (
              <View as="div" margin="0 0 medium 0" textAlign="center">
                {icon}
              </View>
            )}
            <Text size="medium">
              <StepInstructions
                instructions={content}
                replacements={replacements}
              />
            </Text>
          </View>
        </View>
      </Flex>
    </SinglePane>
  );
};

PageInfo.propTypes = {
  /** The error message to display, can be a string or array of objects */
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.object)
  ]).isRequired,
  /** Maximum width of the error message */
  maxWidth: PropTypes.string,
  /** Icon to display */
  icon: PropTypes.node,
  /** Replacements for the error message */
  replacements: PropTypes.object,
};

PageInfo.defaultProps = {
  maxWidth: '550px',
  icon: null,
  replacements: {},
};

export default PageInfo;